import React, { useMemo } from 'react';
import { FileText, Quote } from 'lucide-react';
import {
  getDisplayPlanningSignals,
  groupPlanningSignals,
  type PlanningSignalGroup,
} from '../../../lib/planning/tags';

interface PlanningSignalsEvidenceTabProps {
  application?: Record<string, unknown> | null;
  category?: unknown;
}

const GROUP_LABELS: Record<PlanningSignalGroup, string> = {
  use: 'Use',
  works: 'Works',
  context: 'Context',
  specific: 'Specific',
};

const GROUP_ORDER: PlanningSignalGroup[] = ['use', 'works', 'context', 'specific'];

const SOURCE_LABELS: Record<string, string> = {
  ai: 'AI',
  structural: 'System',
  manual: 'Manual',
  hybrid: 'Mixed',
};

function formatSourceField(value: string): string {
  if (value === 'ai_summary') return 'AI summary';
  if (value === 'form_data') return 'Form data';
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatConfidence(value: number | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return `${Math.round(value * 100)}%`;
}

export default function PlanningSignalsEvidenceTab({
  application,
  category,
}: PlanningSignalsEvidenceTabProps) {
  const signals = useMemo(
    () => getDisplayPlanningSignals(application ?? undefined, { category, surface: 'modal' }),
    [application, category]
  );

  const grouped = useMemo(() => groupPlanningSignals(signals), [signals]);

  if (signals.length === 0) {
    return (
      <section className="rounded-lg border border-dashed border-[var(--c-border)] bg-[var(--c-bg)] p-5 text-sm text-[var(--c-text-secondary)]">
        No planning tags are stored for this application yet.
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-[rgba(var(--c-border-rgb),0.86)] bg-[linear-gradient(180deg,rgba(var(--c-bg-rgb),0.96),rgba(var(--c-surface-rgb),0.62))] p-3.5 shadow-[0_24px_80px_-54px_rgba(0,0,0,0.95)] sm:p-5">
      <div className="mb-5 flex items-center justify-between gap-3 border-b border-[rgba(var(--c-border-rgb),0.55)] pb-4">
        <div className="flex min-w-0 items-baseline gap-2.5">
          <h3 className="font-['Space_Grotesk'] text-lg font-semibold leading-none text-[var(--c-text-primary)]">
            Planning Tags
          </h3>
          <span className="font-['DM_Mono'] text-[11px] leading-none text-[var(--c-text-tertiary)]">
            {signals.length} signal{signals.length === 1 ? '' : 's'}
          </span>
        </div>
        <span
          aria-hidden="true"
          className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[rgba(var(--c-border-rgb),0.8)] bg-[rgba(var(--c-surface-rgb),0.52)] text-[var(--c-text-tertiary)] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
        >
          <FileText className="h-3.5 w-3.5" />
        </span>
      </div>

      <div className="space-y-6">
        {GROUP_ORDER.map((group) => {
          const groupSignals = grouped[group];
          if (!groupSignals.length) return null;

          return (
            <div key={group} className="space-y-3">
              <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                <span className="h-px bg-[linear-gradient(90deg,transparent,rgba(var(--c-border-rgb),0.78))]" />
                <span className="inline-flex items-center gap-2 font-['DM_Mono'] text-[10px] uppercase leading-none tracking-[0.28em] text-[var(--c-text-tertiary)]">
                  {GROUP_LABELS[group]}
                  <span className="rounded-full border border-[rgba(var(--c-border-rgb),0.72)] bg-[rgba(var(--c-surface-rgb),0.58)] px-1.5 py-0.5 text-[9px] tracking-normal text-[var(--c-text-secondary)]">
                    {groupSignals.length}
                  </span>
                </span>
                <span className="h-px bg-[linear-gradient(90deg,rgba(var(--c-border-rgb),0.78),transparent)]" />
              </div>

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {groupSignals.map((signal) => {
                  const confidence = formatConfidence(signal.confidence);
                  const sourceLayer = signal.sourceLayer ? SOURCE_LABELS[signal.sourceLayer] || signal.sourceLayer : null;
                  const visibleEvidence = signal.evidence.slice(0, 2);
                  const hiddenEvidenceCount = Math.max(0, signal.evidence.length - visibleEvidence.length);

                  return (
                    <article
                      key={signal.slug}
                      className="group relative min-h-[164px] overflow-hidden rounded-xl border border-[rgba(var(--c-border-rgb),0.74)] bg-[linear-gradient(180deg,rgba(var(--c-surface-rgb),0.62),rgba(var(--c-bg-rgb),0.74))] p-4 shadow-[0_18px_48px_-38px_rgba(0,0,0,0.92),inset_0_1px_0_rgba(255,255,255,0.035)] transition-colors hover:border-[rgba(var(--c-accent-rgb),0.42)]"
                    >
                      <span
                        aria-hidden="true"
                        className="absolute inset-y-0 left-0 w-[3px] bg-[linear-gradient(180deg,rgba(var(--c-accent-rgb),0.95),rgba(var(--c-accent-rgb),0.18))]"
                      />
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute -right-14 -top-16 h-32 w-32 rounded-full bg-[rgba(var(--c-accent-rgb),0.055)] blur-2xl transition-opacity group-hover:opacity-80"
                      />

                      <div className="relative flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 items-baseline gap-1.5">
                            <span className="font-['DM_Mono'] text-lg font-semibold leading-none text-[var(--c-accent)]">/</span>
                            <h4 className="line-clamp-2 min-w-0 font-['Space_Grotesk'] text-[15px] font-semibold leading-tight text-[var(--c-text-primary)]">
                              {signal.label}
                            </h4>
                          </div>
                        </div>

                        {(confidence || sourceLayer) ? (
                          <div className="flex flex-shrink-0 items-center gap-1.5 pt-0.5 font-['DM_Mono'] text-[9px] uppercase leading-none tracking-[0.16em]">
                            {sourceLayer ? (
                              <span className="text-[var(--c-text-tertiary)]">{sourceLayer}</span>
                            ) : null}
                            {confidence ? (
                              <span className="font-semibold tracking-normal text-[var(--c-accent)]">
                                {confidence}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>

                      <p className="relative mt-2.5 line-clamp-2 text-[13px] leading-relaxed text-[var(--c-text-secondary)]">
                        {signal.evidenceNote || 'Evidence note pending for this stored tag.'}
                      </p>

                      {visibleEvidence.length > 0 ? (
                        <div className="relative mt-3 space-y-1.5">
                          {visibleEvidence.map((item) => (
                            <div
                              key={item}
                              className="flex min-w-0 items-start gap-2 rounded-md border border-[rgba(var(--c-border-rgb),0.68)] bg-[rgba(var(--c-bg-rgb),0.56)] px-2.5 py-2 font-['DM_Mono'] text-[11px] leading-relaxed text-[var(--c-text-secondary)] shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]"
                            >
                              <Quote className="mt-0.5 h-3 w-3 flex-shrink-0 text-[var(--c-accent)]" />
                              <span className="min-w-0 truncate">{item}</span>
                            </div>
                          ))}
                          {hiddenEvidenceCount > 0 ? (
                            <div className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
                              +{hiddenEvidenceCount} more evidence snippet{hiddenEvidenceCount === 1 ? '' : 's'}
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {signal.sourceFields.length > 0 ? (
                        <div className="relative mt-3 flex min-w-0 flex-wrap items-center gap-1.5">
                          {signal.sourceFields.slice(0, 4).map((field, index) => (
                            <span
                              key={field}
                              className="inline-flex max-w-full items-center gap-1 rounded-md border border-[rgba(var(--c-border-rgb),0.56)] bg-[rgba(var(--c-surface-rgb),0.38)] px-1.5 py-0.5 text-[10px] leading-none text-[var(--c-text-tertiary)]"
                            >
                              {index === 0 ? <FileText className="h-3 w-3 flex-shrink-0" /> : null}
                              <span className="truncate">{formatSourceField(field)}</span>
                            </span>
                          ))}
                          {signal.sourceFields.length > 4 ? (
                            <span className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
                              +{signal.sourceFields.length - 4}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
